
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Product } from "@/types/product";

export const initialProducts: Product[] = [
  {
    id: "1",
    title: "Absence Tracking",
    description: "Track and manage student absences efficiently",
    features: "• Real-time absence tracking\n• Custom absence categories\n• Automated notifications",
    link: "https://example.com/absence",
    logo: "https://example.com/absence-logo.png",
    visible: true
  },
  {
    id: "2",
    title: "Class Management",
    description: "Comprehensive class and schedule management",
    features: "• Class scheduling\n• Student roster management\n• Attendance tracking",
    link: "https://example.com/class",
    logo: "https://example.com/class-logo.png",
    visible: true
  },
  {
    id: "3",
    title: "Student Information",
    description: "Centralized student data management system",
    features: "• Student profiles\n• Academic records\n• Contact information",
    link: "https://example.com/student",
    logo: "https://example.com/student-logo.png",
    visible: true
  },
  {
    id: "4",
    title: "Executive Reports",
    description: "Advanced analytics and reporting tools",
    features: "• Custom report generation\n• Data visualization\n• Export capabilities",
    link: "https://example.com/reports",
    logo: "https://example.com/reports-logo.png",
    visible: true
  },
  {
    id: "5",
    title: "Financial Applications",
    description: "Complete financial management solution",
    features: "• Budget tracking\n• Payment processing\n• Financial reporting",
    link: "https://example.com/finance",
    logo: "https://example.com/finance-logo.png",
    visible: true
  }
];

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*');

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedProducts = data.map(product => ({
          id: product.id,
          title: product.name,
          description: product.description || "",
          features: product.features || "",
          link: product.link || "",
          logo: product.logo || "",
          product_screen: product.product_screen || "",
          visible: product.visible
        }));

        setProducts(formattedProducts);
      } else {
        const { error: insertError } = await supabase
          .from('products')
          .insert(initialProducts.map(product => ({
            name: product.title,
            description: product.description,
            features: product.features,
            link: product.link,
            logo: product.logo,
            visible: product.visible
          })));

        if (insertError) throw insertError;
        setProducts(initialProducts);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to load products.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []); // Add useEffect to fetch products on mount

  const handleDelete = async (id: string) => {
    try {
      const { data: plans, error: plansError } = await supabase
        .from('plans')
        .select('id')
        .eq('feature_id', id);

      if (plansError) throw plansError;

      if (plans && plans.length > 0) {
        toast({
          title: "Cannot Delete Product",
          description: "This product has associated plans. Please delete the plans first.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProducts(products.filter(product => product.id !== id));
      toast({
        title: "Success",
        description: "Product deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product.",
        variant: "destructive",
      });
    }
  };

  return {
    products,
    isLoading,
    fetchProducts,
    handleDelete
  };
};
